// inventoryFlow.js — per-inventory bid→GAM→render flow + anomaly detection.
//
// Consumes the slot lifecycles produced by AdLifecycleView's buildSlotLifecycles()
// (the shared correlation engine — hb_cache_id / bidder+price / gpid / timing linkage)
// and rolls them up by GAM ad unit into the inventory scoreboard the Bid Health panel
// shows: per unit — Prebid/APS bid-back, passed-to-GAM, AdX-outbid, HB-rendered — plus
// the two anomalies a tester hunts for:
//
//   • render-fail  — HB bid was passed to GAM AND beat the winner on price, yet no HB
//                    creative rendered (latency / render timeout). Per-slot; uses the
//                    normal cust_params linkage.
//   • leak         — a Prebid winning bid whose hb_cache_id reached NO GAM request:
//                    the bid came back client-side but never got sent to the ad server.
//                    This is AUCTION-level, not slot-level: a leaked bid has no cust_params
//                    value to link it to a slot, so it can't be attributed to one reliably.
//                    We attribute best-effort via the Prebid imp's ext.gpid when present.
//
// Pure function — no React, no imports. Takes lifecycles + raw rows, returns plain data.

function rowType(r) {
  return (r.type || r.requestType || '').toLowerCase();
}

// Parse GAM cust_params (URL-encoded) → flat object. Cust_params is not persisted on
// validData, so always read from the raw URL (see CLAUDE.md gotcha).
function custParams(url) {
  try {
    const raw = new URL(url).searchParams.get('cust_params') || '';
    return Object.fromEntries(new URLSearchParams(decodeURIComponent(raw)));
  } catch {
    return {};
  }
}

// All hb_cache_id values (primary + bidder-scoped hb_cache_id_*) present in a GAM request.
function gamCacheIds(gamRow) {
  const cp = custParams(gamRow.url || '');
  const ids = [];
  for (const [k, v] of Object.entries(cp)) {
    if (v && (k === 'hb_cache_id' || k.startsWith('hb_cache_id_'))) ids.push(v);
  }
  return ids;
}

// True if the GAM request carried ANY Prebid targeting — plain OR bidder-scoped
// (hb_pb / hb_pb_<bidder> / hb_cache_id / hb_cache_id_<bidder>). Read from the raw
// URL because expanded.hbPb / expanded.prebidId capture ONLY the plain keys, so
// send-all-bids traffic that uses only bidder-scoped keys (e.g. hb_pb_thetradedesk)
// was being under-counted as "not passed to GAM". Mirrors gamCacheIds()'s scan.
function gamPassedPrebid(gamRow) {
  const cp = custParams(gamRow?.url || '');
  for (const [k, v] of Object.entries(cp)) {
    if (!v) continue;
    if (k === 'hb_pb' || k.startsWith('hb_pb_')) return true;
    if (k === 'hb_cache_id' || k.startsWith('hb_cache_id_')) return true;
  }
  return false;
}

// Who rendered this slot: proxy creativeSource is authoritative; fall back to cust_params.
function renderedBy(lc) {
  const src = lc?.phases?.gam?.row?.validData?.expanded?.creativeSource?.source;
  if (src) return src; // 'prebid' | 'amazon' | 'adx' | 'direct' | 'no-fill'
  const gam = lc?.phases?.gam || {};
  if (gam.hbBidder || gam.hbPb || gam.cacheId) return 'prebid';
  if (lc?.phases?.amazon?.passedToGAM) return 'amazon';
  return 'adx';
}

// Prebid returned a real bid for this slot's linked auction.
function prebidBidBack(lc) {
  const p = lc?.phases?.prebid;
  if (!p?.fired) return false;
  if (p.winner && Number(p.winner.price) > 0) return true;
  return Array.isArray(p.bids) && p.bids.some((b) => {
    const s = (b.status || '').toLowerCase();
    return (s === 'bid' || s === 'won') && Number(b.price) > 0;
  });
}

// The client-side winning HB CPM for this slot (what AdX had to beat).
function hbCpm(lc) {
  const p = lc?.phases?.prebid;
  if (p?.winner && p.winner.price != null && !isNaN(Number(p.winner.price))) return Number(p.winner.price);
  const hbPb = Number(lc?.phases?.gam?.hbPb);
  return isNaN(hbPb) ? null : hbPb;
}

// Impression-match tiers trustworthy enough to headline a slow-render incident —
// mirrors AdLifecycleView's Session Quality Alert so the two counts reconcile.
// Nearest-time-only matches are excluded so a mis-bound beacon on a repeated iu
// can't inflate the render-problem dollars.
const SLOW_CONFIDENT_TIERS = ['xai', 'sai', 'impCorrelator', 'correlator', 'adUnit'];
const SLOW_RENDER_MS = 30000;

// Creative painted, but >30s after the GAM request — the slot was viewable yet
// effectively blank. A timing failure, independent of who rendered; a slow HB
// render still counts (it would otherwise read as a clean 'rendered' success).
function slowRenderMsOf(lc) {
  const imp = lc?.phases?.impression;
  if (imp?.fired && imp.latencyMs > SLOW_RENDER_MS && SLOW_CONFIDENT_TIERS.includes(imp.matchTier)) {
    return imp.latencyMs;
  }
  return null;
}

function classify(lc) {
  const rendered = renderedBy(lc);

  const pb = prebidBidBack(lc);
  const aps = lc?.phases?.amazon?.bidStatus === 'bid' ||
    (!!lc?.phases?.amazon?.fired && !!lc?.phases?.amazon?.passedToGAM);
  // Prefer the raw-URL scan (handles bidder-scoped hb_pb_*/hb_cache_id_*), fall
  // back to the parsed plain-key lifecycle fields when the row URL is unavailable.
  const passed = gamPassedPrebid(lc?.phases?.gam?.row) ||
    !!(lc?.phases?.gam?.hbPb || lc?.phases?.gam?.cacheId);
  const win = lc?.phases?.gam?.winnerCpm;
  const hb = hbCpm(lc);

  // No fill. Carry the demand flags through instead of short-circuiting on them:
  // a no-fill on a slot that HAD a Prebid/APS bid is a revenue leak (line item /
  // price bucket / targeting), while a no-fill on a slot nobody bid on is just
  // empty inventory. `wastedDemand` is what separates the two — see bidNoFill.js.
  if (rendered === 'no-fill') {
    return { rendered, anomaly: 'nofill', pb, aps, passed, hbCpm: hb, wastedDemand: pb || aps };
  }

  const hbRendered = rendered === 'prebid' || rendered === 'amazon';
  if (hbRendered) return { rendered, anomaly: 'rendered', pb, aps, passed, winnerCpm: win, hbCpm: hb };

  // Passed to GAM and beat the winner on price, yet a non-HB creative rendered → render-fail.
  if (passed && hb != null && win != null && hb >= win) {
    return { rendered, anomaly: 'latency', pb, aps, passed, winnerCpm: win, hbCpm: hb };
  }
  // AdX rendered over a real HB bid it outbid on price.
  if (pb && win != null && hb != null && win > hb) {
    return { rendered, anomaly: 'outbid', pb, aps, passed, winnerCpm: win, hbCpm: hb };
  }
  if (!pb && !aps) return { rendered, anomaly: 'nobid', pb, aps, passed, winnerCpm: win, hbCpm: hb };
  return { rendered, anomaly: 'outbid', pb, aps, passed, winnerCpm: win, hbCpm: hb };
}

export function computeInventoryFlow(lifecycles = [], rows = []) {
  // ── Per-unit rollup from lifecycles ──────────────────────────────────
  const units = new Map();
  const perSlot = [];
  for (const lc of lifecycles) {
    const iu = lc.adUnit || '(unknown)';
    const c = classify(lc);
    c.slowRenderMs = slowRenderMsOf(lc);
    perSlot.push({ iu, key: lc.key, ...c });
    let u = units.get(iu);
    if (!u) { u = { iu, reqs: 0, pbBack: 0, apsBack: 0, passed: 0, outbid: 0, renderFail: 0, rendered: 0, nofill: 0, wastedDemand: 0, slowRender: 0, hbCpmMax: 0, hbBids: 0, gamCpmMax: 0, gamWins: 0 }; units.set(iu, u); }
    u.reqs += 1;
    if (c.pb) u.pbBack += 1;
    if (c.aps) u.apsBack += 1;
    if (c.passed) u.passed += 1;
    if (c.anomaly === 'outbid') u.outbid += 1;
    if (c.anomaly === 'latency') u.renderFail += 1;
    if (c.anomaly === 'rendered') u.rendered += 1;
    if (c.anomaly === 'nofill') u.nofill += 1;
    // No-fill despite a bid being on the table — the revenue-leak subset of nofill.
    if (c.wastedDemand) u.wastedDemand += 1;
    // Timing render-problem, independent of the source-based anomaly above.
    if (c.slowRenderMs != null) u.slowRender += 1;
    // Raw HB demand: track the highest winning HB bid CPM seen for the unit,
    // so the collapsed row can show its peak HB bid price.
    if (c.hbCpm != null && c.hbCpm > 0) { u.hbCpmMax = Math.max(u.hbCpmMax, c.hbCpm); u.hbBids += 1; }
    // GAM winning price (AdX / line-item) — peak winnerCpm across the unit's slots.
    if (c.winnerCpm != null && c.winnerCpm > 0) { u.gamCpmMax = Math.max(u.gamCpmMax, c.winnerCpm); u.gamWins += 1; }
  }

  // ── Auction-level leak check (reverse cache-id) ──────────────────────
  // Every hb_cache_id that actually reached a GAM request:
  const gamRows = rows.filter((r) => rowType(r) === 'google');
  const reached = new Set();
  for (const g of gamRows) for (const id of gamCacheIds(g)) reached.add(id);

  const leaks = [];
  for (const r of rows) {
    if (rowType(r) !== 'prebid') continue;
    const bids = r.validData?.tables?.bids;
    if (!Array.isArray(bids) || !bids.length) continue;
    // winning bid = highest price with a cache id
    const winner = bids
      .filter((b) => Number(b.price) > 0 && (b.cache || b.hb?.hb_cache_id))
      .reduce((w, b) => (Number(b.price) > Number(w?.price || 0) ? b : w), null);
    if (!winner) continue;
    const cacheId = winner.cache || winner.hb?.hb_cache_id;
    if (cacheId && !reached.has(cacheId)) {
      // best-effort attribution to an ad unit via the Prebid imp's gpid
      const imps = r.validData?.tables?.imps || [];
      const gpid = imps.find((i) => i.gpid && i.gpid !== '-')?.gpid || null;
      leaks.push({ rowKey: r._k ?? r.id, seat: winner.seat || null, cpm: Number(winner.price), cacheId, iu: gpid });
      if (gpid && units.has(gpid)) units.get(gpid).leak = (units.get(gpid).leak || 0) + 1;
    }
  }

  // ── Totals ───────────────────────────────────────────────────────────
  const list = [...units.values()];
  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const sum = (k) => list.reduce((a, u) => a + (u[k] || 0), 0);
  const totalReqs = sum('reqs');
  const totals = {
    units: list.length,
    reqs: totalReqs,
    pbBack: sum('pbBack'),
    apsBack: sum('apsBack'),
    passed: sum('passed'),
    outbid: sum('outbid'),
    renderFail: sum('renderFail'),
    slowRender: sum('slowRender'),
    rendered: sum('rendered'),
    nofill: sum('nofill'),
    wastedDemand: sum('wastedDemand'),
    leaks: leaks.length,
    pbBackRate: pct(sum('pbBack'), totalReqs),
    apsBackRate: pct(sum('apsBack'), totalReqs),
    renderRate: pct(sum('rendered'), sum('passed')),
  };

  return {
    units: list.sort((a, b) => (b.leak || 0) - (a.leak || 0) || b.renderFail - a.renderFail || b.reqs - a.reqs),
    perSlot,
    leaks,
    totals,
  };
}

export default computeInventoryFlow;
